/**
 * The Brand Kits GA4 events. Each one fires once its action has landed on the server, so a refused add or delete
 * is not counted; the only exceptions are the template actions that leave the page, which are counted on the click.
 */

import {BrandAssetType, FontType} from '@Components/mystuff-brands/brand-kits.types';
import type {NewBrandKitGA4Source} from '@Libraries/ga-events';
import {AddBrandAssetGA4Source, BrandKitAssetGA4Type, BrandKitAssetsOpenedGA4Source, GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import type {BrandAssetAddedGA4Event} from '@Libraries/brand-kit-ga4.types';

const BRAND_KIT_ASSET_GA4_TYPES: Record<BrandAssetType, BrandKitAssetGA4Type> = {
  [BrandAssetType.BRAND_LOGOS]: BrandKitAssetGA4Type.LOGOS,
  [BrandAssetType.BRAND_COLORS]: BrandKitAssetGA4Type.COLORS,
  [BrandAssetType.BRAND_FONTS]: BrandKitAssetGA4Type.FONTS,
  [BrandAssetType.BRAND_IMAGES]: BrandKitAssetGA4Type.IMAGES,
  [BrandAssetType.BRAND_VOICE]: BrandKitAssetGA4Type.VOICE,
  [BrandAssetType.BRAND_VIDEOS]: BrandKitAssetGA4Type.VIDEOS,
  [BrandAssetType.BRAND_TEMPLATES]: BrandKitAssetGA4Type.TEMPLATES,
};

/** The fonts card picks for these three slots only; a font of another type never comes from it. */
const FONT_SLOT_GA4_SOURCES: Partial<Record<FontType, AddBrandAssetGA4Source>> = {
  [FontType.HEADING]: AddBrandAssetGA4Source.HEADING,
  [FontType.SUB_HEADING]: AddBrandAssetGA4Source.SUBHEADING,
  [FontType.BODY]: AddBrandAssetGA4Source.BODY,
};

/** An asset type opened from its card, or from its step in the setup checklist, on the Brand Kits page. */
export const trackBrandKitAssetsOpened = (brandAssetType: BrandAssetType): void => {
  trackGA4CustomEvent(GA4EventName.BRAND_KIT_ASSETS_OPENED, {
    [GA4EventParamName.SOURCE]: BrandKitAssetsOpenedGA4Source.BRAND_KITS_PAGE,
    [GA4EventParamName.TYPE]: BRAND_KIT_ASSET_GA4_TYPES[brandAssetType],
  });
};

export const trackBrandAssetAdded = ({brandAssetType, source, value}: BrandAssetAddedGA4Event): void => {
  trackGA4CustomEvent(GA4EventName.ADD_BRAND_ASSET, {
    [GA4EventParamName.TYPE]: BRAND_KIT_ASSET_GA4_TYPES[brandAssetType],
    [GA4EventParamName.SOURCE]: source,
    ...(value === undefined ? {} : {[GA4EventParamName.VALUE]: value}),
  });
};

/** One AddBrandAsset per asset, for a batch of adds that all came the same way. */
export const trackBrandAssetsAdded = (addedAsset: BrandAssetAddedGA4Event, addedCount: number): void => {
  for (let added = 0; added < addedCount; added += 1) {
    trackBrandAssetAdded(addedAsset);
  }
};

/** A font picked into one of the fonts card's slots; the slot is the event's source. */
export const trackBrandFontAdded = (fontType: FontType, fontName: string): void => {
  const source = FONT_SLOT_GA4_SOURCES[fontType];

  if (source === undefined) {
    return;
  }

  trackBrandAssetAdded({brandAssetType: BrandAssetType.BRAND_FONTS, source, value: fontName});
};

export const trackNewBrandKit = (source: NewBrandKitGA4Source): void => {
  trackGA4CustomEvent(GA4EventName.NEW_BRAND_KIT, {[GA4EventParamName.SOURCE]: source});
};

export const trackBrandKitRenamed = (): void => {
  trackGA4CustomEvent(GA4EventName.EDIT_BRAND_NAME);
};

export const trackBrandKitSetAsDefault = (): void => {
  trackGA4CustomEvent(GA4EventName.SET_DEFAULT_BRAND_KIT);
};

export const trackBrandKitDeleted = (): void => {
  trackGA4CustomEvent(GA4EventName.DELETE_BRAND_KIT);
};

export const trackBrandAssetDeleted = (brandAssetType: BrandAssetType): void => {
  trackGA4CustomEvent(GA4EventName.DELETE_BRAND_ASSET, {[GA4EventParamName.TYPE]: BRAND_KIT_ASSET_GA4_TYPES[brandAssetType]});
};
